/**
 * Model prices and the AI spend defaults, one source for the backend and the
 * admin (ux-admin-07). `MODEL_COST_MAP` is the backend's cost fallback when
 * OpenRouter does not report a cost (`estimateCost` in
 * `packages/functions/src/ai/model-costs.ts`), the per-call reservation of the
 * Anthropic daily cap, and the price of every projection in the admin
 * (Benchmark, Simulator). `node scripts/check-model-costs.mjs` checks it
 * against the live OpenRouter catalog: keep each entry on one line.
 */
/**
 * Retail cost per token in USD. Source: OpenRouter pricing page.
 * Always retail prices regardless of free-tier availability: the free tier is
 * a subsidy that can be revoked at any time and must not appear in cost
 * projections.
 */
export const MODEL_COST_MAP = {
    "google/gemini-2.0-flash-001:free": { promptPerToken: 0.0000001, completionPerToken: 0.0000004 },
    "google/gemini-2.0-flash-001": { promptPerToken: 0.0000001, completionPerToken: 0.0000004 },
    "google/gemini-2.5-flash-lite": { promptPerToken: 0.0000001, completionPerToken: 0.0000004 },
    "google/gemini-2.5-flash:free": { promptPerToken: 0.00000015, completionPerToken: 0.0000006 },
    "google/gemini-2.5-flash": { promptPerToken: 0.0000003, completionPerToken: 0.0000025 },
    "google/gemini-flash-1.5-8b:free": { promptPerToken: 0.0000000375, completionPerToken: 0.00000015 },
    "google/gemini-flash-1.5-8b": { promptPerToken: 0.0000000375, completionPerToken: 0.00000015 },
    "anthropic/claude-haiku-4-5": { promptPerToken: 0.0000008, completionPerToken: 0.000004 },
    // evaluate_job runs here via BYOK: OpenRouter bills $0 and the real charge
    // arrives as upstream_inference_cost; this is the fallback when it does not.
    "anthropic/claude-haiku-5.5": { promptPerToken: 0.0000001, completionPerToken: 0.0000005 },
    "openai/gpt-4o-mini": { promptPerToken: 0.00000015, completionPerToken: 0.0000006 },
    "openai/gpt-4.1-nano": { promptPerToken: 0.0000001, completionPerToken: 0.0000004 },
    "meta-llama/llama-3.3-70b-instruct": { promptPerToken: 0.00000012, completionPerToken: 0.0000003 },
    // TypeSafe Jev (decision model): billed on input only, output tokens are free.
    // The API echoes a dated revision, so both slugs are listed.
    "typesafe/jev-1.13": { promptPerToken: 0.000000042, completionPerToken: 0 },
    "typesafe/jev-1.13-20260917": { promptPerToken: 0.000000042, completionPerToken: 0 },
    // DeepSeek family — these were missing entirely, so their calls recorded $0.
    "deepseek/deepseek-v4-flash": { promptPerToken: 0.0000000419, completionPerToken: 0.0000000837 },
    "deepseek/deepseek-v4.1-flash": { promptPerToken: 0.00000003, completionPerToken: 0.0000005 },
    "deepseek/deepseek-v4-flash-0731": { promptPerToken: 0.0000000108, completionPerToken: 0.00000128 },
    "~deepseek/deepseek-v4-flash-latest": { promptPerToken: 0.0000000108, completionPerToken: 0.00000128 },
};
/** Price of `modelId` per token, or null when the map does not list it (never another model's price). */
export function modelTokenCost(modelId) {
    return Object.prototype.hasOwnProperty.call(MODEL_COST_MAP, modelId) ? MODEL_COST_MAP[modelId] : null;
}
/** Per-token price × 1M without float noise (0.0000003 × 1e6 = 0.3, not 0.30000000000000004). */
function per1M(perToken) {
    return Math.round(perToken * 1e6 * 1e9) / 1e9;
}
/** Price of `modelId` per million tokens, or null when the map does not list it. */
export function modelPricePer1M(modelId) {
    const cost = modelTokenCost(modelId);
    return cost ? { inputPer1M: per1M(cost.promptPerToken), outputPer1M: per1M(cost.completionPerToken) } : null;
}
/**
 * Model of every text and vision call when `config/global` has no
 * `textModelId` / `visionModelId`, and of `evaluate_job` without
 * `evaluateJobModelId` (`DEFAULT_TEXT_MODEL` in `ai/openrouter.ts`).
 */
export const DEFAULT_AI_MODEL_ID = "anthropic/claude-haiku-5.5";
/**
 * Where a failed call goes when `config/global` sets no paid fallback
 * (`DEFAULT_MODEL_BASE` in `ai/openrouter.ts`), and what the chat answers on
 * past the Anthropic daily cap.
 */
export const DEFAULT_AI_FALLBACK_MODEL_ID = "google/gemini-2.5-flash-lite";
/**
 * OpenRouter's free-models router: no `:free` suffix, but it only routes to
 * free endpoints, like a `:free` slug.
 */
export const FREE_ROUTER_MODEL_ID = "openrouter/free";
/**
 * A model served by OpenRouter's free endpoints (a `:free` slug or the free
 * router). Those endpoints may keep prompts and train on them: choosing one
 * asks for an explicit confirmation, and a vanished one is replaced by a paid
 * model (spec 04 › Privacidad de los proveedores).
 */
export function isFreeTierModelId(modelId) {
    const id = modelId.trim();
    return id.endsWith(":free") || id === FREE_ROUTER_MODEL_ID;
}
/**
 * Model a failed text or vision call on `modelId` moves to, as
 * `chatCompletion` / `visionCompletion` pick it: the configured paid
 * fallback, else Flash Lite; none when the call already runs on Flash Lite.
 */
export function effectiveFallbackModelId(modelId, paidFallbackModelId) {
    if (paidFallbackModelId)
        return paidFallbackModelId;
    return modelId === DEFAULT_AI_FALLBACK_MODEL_ID ? null : DEFAULT_AI_FALLBACK_MODEL_ID;
}
/**
 * Model the assistant chat answers on once the Anthropic daily cap is spent,
 * as `anthropicBudgetSubstitute` in `ai/openrouter.ts` picks it: the
 * configured paid fallback, unless it is an Anthropic model itself (it would
 * hit the same cap); else Flash Lite.
 */
export function chatBudgetSubstituteModelId(paidFallbackModelId) {
    return paidFallbackModelId && !paidFallbackModelId.startsWith("anthropic/")
        ? paidFallbackModelId
        : DEFAULT_AI_FALLBACK_MODEL_ID;
}
/**
 * Daily USD cap of every call to an Anthropic model (`anthropic/*`), UTC day,
 * all lanes together. `config/global.evaluateJobModelDailyBudgetUsd`
 * overrides it (spec 04 › Tope diario de Haiku).
 */
export const EVALUATE_JOB_MODEL_DAILY_BUDGET_USD = 2.7;
/**
 * Part of that cap only the assistant chat may spend
 * (`config/global.chatModelReserveUsd` overrides it): background work stops
 * at the cap minus this reserve and waits for the next UTC day.
 */
export const CHAT_MODEL_RESERVE_USD = 0.4;
/**
 * Default of `config/global.interactiveModelReserveUsd`: part of the
 * background limit the daily evaluation drain may not spend, kept for work a
 * person waits for (CV, letter, prep, «Evaluar», import, CV parsing). 0 means
 * no reserve: the drain stops where every background call does (spec 04 ›
 * Colchón interactivo).
 */
export const INTERACTIVE_MODEL_RESERVE_USD = 0;
/**
 * Default of `config/global.aiDailyBudgetUsd`: the day's spend on models that
 * are not Anthropic's (Flash Lite, a configured fallback, Jev: none has a
 * hard cap) past which `aiBudgetGuard` raises `aiDailyBudgetExceeded`. It
 * only alerts. Until 2026-10-28 it was 2 against every model's spend, below
 * what the Anthropic cap above allows by design, so it fired on normal days
 * (q-fn-scheduled-05).
 */
export const AI_DAILY_ALERT_BUDGET_USD = 1;
/**
 * Anthropic spend alerts past its cap plus this share of it (2.70 → 2.97):
 * the cap already stops work, so spend past it means a leak in the gate
 * (a call that skipped the reservation, a settlement that went wrong).
 */
export const ANTHROPIC_SPEND_ALERT_MARGIN = 0.1;
//# sourceMappingURL=model-costs.js.map